import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { ReplayState, State } from '../../../models/State';
import { BackendService } from '../../../common/services/backend.service';
import { FactoryService } from '../../../common/services/factory.service';
import { Interview } from '../../../models/Interview';
import { InterviewStreamService } from './interview.service';
import { mergeMap, shareReplay, map } from 'rxjs/operators';

declare var window: any;

type ReviewerSettings = {
    hasAvailableReviewerTypes: boolean;
    reviewerTypes: string[];
    reviewerStaff: Record<string, string>;
}

@Injectable()
export class InterviewDataService {
    navigator = <any>navigator;
    audioContext = null;
    meter = null;
    autoplay: boolean;
    answers: boolean;
    repeat: boolean;
    practice: any;
    test: any;
    attemptId: any;
    interviewId: any;
    hasAvailableReviewerTypes: boolean = false;
    reviewerStaff: Record<string, string>;
    reviewerTypes: string[] = [];
    aiFeedbackRequested: boolean = false;
    
    private reviewerSettings$?: Observable<any>;
    private readonly rState: State;
    private dataStore = <any>{};
    private _interview: BehaviorSubject<Interview> = new BehaviorSubject(null);
    private _attempts: BehaviorSubject<[]> = new BehaviorSubject(null);
    private _attempt: BehaviorSubject<[]> = new BehaviorSubject(null);
    private readonly _passcode: BehaviorSubject<any>;
    private _state: BehaviorSubject<State> = new BehaviorSubject(new State());

    constructor(
        private backendService: BackendService,
        private factory: FactoryService,
        private _videoService: InterviewStreamService
    ) {
        this.rState = new State();
        this.dataStore = <any>{interview: [], attempts: [], attempt: []};
        this._passcode = new BehaviorSubject(null);
    }

    get state(): Observable<State> {
        return this._state.asObservable();
    }

    get reviewStates(): Observable<ReplayState> {
        return this.state.pipe(map(state => state.replayStates));
    }

    get interview(): Observable<any> {
        return this._interview.asObservable();
    }

    get attempts(): Observable<[]> {
        return this._attempts.asObservable();
    }

    get attempt(): Observable<[]> {
        return this._attempt.asObservable();
    }

    get passcode(): Observable<[]> {
        return this._passcode.asObservable();
    }

    getInterview(id: any = this.interviewId) {
        let params = '?video_interview_id=' + id;
        if (window.parent.USER_TYPE === 'student') {
            params += '&student=' + window.parent.CURRENT_USER;
        }

        return this.backendService.getAll('?practice=' + this.practice, 'video-interview/' + id)
            .pipe(shareReplay(), mergeMap(res => {
                return this.backendService.getAll(params, 'video-interview-attempt')
                .pipe(map(attempts => {
                    res.attempts = attempts['models'];
                    return res;
                }))
            }))
            .subscribe((res: Response) => {
                    if (res) {
                        this.dataStore.interview = res;
                        this.dataStore.attempts = res['attempts'];
                        this._interview.next(this.factory.interviewFactory(res));
                        this._attempts.next(Object.assign({}, this.dataStore).attempts);
                    }
                },
                error => console.log('Could not fetch interview questions with request status', error));
    }

    getInterviewStats(id: any = this.interviewId) {
        return this.backendService.getAll('', 'video-interview/' + id + '/stats');
    }

    getAllAttempts(id: any) {
        let params = '?video_interview_id=' + id + '&student=' + window.parent.CURRENT_USER;
        return this.backendService.getAll(params, 'video-interview-attempt')
            .subscribe((res: Response) => {
                if (res) {
                    this.dataStore.attempts = res['models'];
                    this._attempts.next(Object.assign({}, this.dataStore).attempts);
                }
            });
    }

    getAttempt(id:any = this.attemptId, token?) {
        if (token)
            id = id + '?t=' + token;
        return this.backendService.getOne(id, 'video-interview-attempt')
            .subscribe((res: any) => {
                this.dataStore.attempt = [];
                if (res) {
                    let notFound = true;
                    this.dataStore.attempts.forEach((item, index) => {
                        if (item.video_interview_attempt_id === res.video_interview_attempt_id) {
                            this.dataStore.attempt[0] = res;
                            notFound = false;
                        }
                    });

                    if (notFound)
                        this.dataStore.attempt.unshift(res);

                    this._attempt.next(Object.assign({}, this.dataStore).attempt);
                }
            });
    }

    getPasscode() {
        return this.backendService.getAll('', 'video-interview-attempt/' + this.attemptId + '/passcode')
            .subscribe(passcodes => {
                this._passcode.next(passcodes);
            }, error => console.log('Could not retrieve passcode.'));
    }

    editFeedback() {
        return this.backendService.getOne(this.attemptId, 'video-interview-attempt')
            .subscribe((res: Response) => {
                this._interview.next(Object.assign({}, this.dataStore).interview);
            }, error => console.log('Could not load todo.'));
    }

    saveAttempt(data) {
        return this.backendService.create('video-interview-attempt', data)
            .subscribe(resp => {
                this.attemptId = resp.video_interview_attempt_id;
                if (resp) {
                    let notFound = true;
                    this.dataStore.attempts.forEach((item, index) => {
                        if (item.video_interview_attempt_id === resp.video_interview_attempt_id) {
                            this.dataStore.attempts[0] = resp;
                            notFound = false;
                        }
                    });

                    if (notFound) {
                        this.dataStore.attempts.push(resp);
                    }
                    this._attempts.next(Object.assign({}, this.dataStore).attempts);
                }

            }, error => console.log('Could not save answer', error));
    }

    updateAttempt(obj) {
            obj.video_interview_attempt_id = this.attemptId;
        return this.backendService.update(obj.video_interview_attempt_id, 'video-interview-attempt', obj)
            .subscribe(data => {
                if (obj.passcode || obj.global_passcode)
                    this._passcode.next({passcode: obj.passcode, global_passcode : obj.global_passcode});
            }, error => console.log('Could not save answer', error));

    }

    saveAnswer(id) {
        let data = this.formatAnswerData(id);
        return this.backendService.updateNested(this.attemptId, 'video-interview-attempt', data, 'answer');
    }

    savePasscode(data) {
        return this.backendService.create('share-interview', data)
            .subscribe(updateReponseObj => {

                },
                error => console.log('Could not save passcode with request status', error))
    }

    deleteInterview(id) {
        return this.backendService.deleteOne(id, 'video-interview-attempt')
            .subscribe(updateReponseObj => {
                    this.dataStore.attempts.forEach((attempt, index) => {
                        if (attempt.video_interview_attempt_id === id ) {
                            this.dataStore.attempts.splice(index, 1);
                            if (this.dataStore.attempts.length > 0)
                                this.attemptId = this.dataStore.attempts[0].video_interview_attempt_id;
                        }
                    });

                     this._attempts.next(Object.assign({}, this.dataStore).attempts)

                    if (this.dataStore.attempts.length > 0) {
                        this.getInterview(this.interviewId);
                    } else {
                        let tab = sessionStorage.getItem('tab');
                        window.parent.location.href = '/students/index.php?s=resource&ss=video_interviews&mode=list&tab=' + tab;
                    }
                },
                error => console.log('Could not delete interview with request status', error))
    }

    requestFeedback(data, id:any = this.attemptId) {
        return this.backendService.updateNested(id, 'video-interview-attempt', data, 'request-feedback')
            .subscribe(updateReponseObj => {
                    let data = <any>{};
                    if (window.parent.USER_TYPE == 'student') {
                        data.student = window.parent.CURRENT_USER;
                        data.status = updateReponseObj.status._id;
                        this.updateAttempt(data);
                    }
                    this.getAttempt(id);
                },
                error => console.log('Could not request feedback with request status', error))
    }

    updateResponseState(state) {
        switch (state) {
            case 'autoplay':
                this.autoplay = !this.autoplay;
                break;
            case 'answers':
                this.answers = !this.answers;
                break;
            case 'repeat':
                this.repeat = !this.repeat;
                break;
        }
        this.rState.replayStates.setAll(this.autoplay, this.answers, this.repeat);
        this._state.next(Object.assign({}, this.rState));
    }

    formatAnswerData(wrapper_id) {

        let data = <any>{};
        data.question_wrapper_id = wrapper_id;
        data.tmp_file_name = this._videoService.getStreamName + '.mp4';
        return data;
    }

    formatData(payload: any) {

        let data = <any>{};
        data.review = payload.review;
        data.video_interview_question_wrapper_id = payload.video_interview_question_wrapper_id;
        data.rating = payload.rating;
        data.reviewer = payload.reviewer;

        if (payload.t)
            data.t = payload.t;

        if (payload.video_interview_feedback_id) {
            data.video_interview_feedback_id = payload.video_interview_feedback_id;
        }

        return data;
    }

    loadReviewerSettings() {
        if (!this.reviewerSettings$) {
            this.reviewerSettings$ = this.backendService.getAll('reviewer-settings', 'video-interview/').pipe(
                shareReplay(1)
            );
        }
        this.reviewerSettings$.subscribe((settings: ReviewerSettings) => {
            this.hasAvailableReviewerTypes = settings.hasAvailableReviewerTypes;
            this.reviewerTypes = settings.reviewerTypes;
            this.reviewerStaff = settings.reviewerStaff;
        });
    }
}

